import React, { Component } from 'react';
import { Cta } from './Cta';
import { Reveal } from './Reveal';
import { PathThread } from './PathThread';
import { useScore } from './ScoreContext';
import { Compass, Route, Footprints, DoorOpen, Eye, PenLine } from 'lucide-react';
export function Offer() {
  const {
    answeredCount
  } = useScore();
  const ctaLabel = answeredCount === 0 ? 'Get Your Reader-to-Revenue Score' : answeredCount < 4 ? 'Continue Your Score' : 'Finish Your Score';
  const handleCtaClick = () => {
    document.getElementById('final-score-block')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const buildComponents = [{
    label: 'Reader diagnostic',
    icon: Compass
  }, {
    label: 'Guided path',
    icon: Route
  }, {
    label: 'Useful next steps',
    icon: Footprints
  }, {
    label: 'Offer paths',
    icon: DoorOpen
  }, {
    label: 'What readers cared about',
    icon: Eye
  }, {
    label: 'Content and sales assets',
    icon: PenLine
  }];
  const futureQuestions = ['Course', 'Certification', 'Workshop', 'Event', 'Advisory', 'Team implementation', 'Sales system', 'Content system'];
  const proof = [{
    title: 'Reader sees where they are',
    Visual: () => <div className="flex items-center justify-center gap-2 w-full" data-id="element-269">
          <div className="w-2.5 h-2.5 rounded-full bg-gray-200" data-id="element-270"></div>
          <div className="h-px w-8 bg-gray-200" data-id="element-271"></div>
          <div className="w-4 h-4 rounded-full bg-seaglass ring-4 ring-seaglass/20" data-id="element-272"></div>
          <div className="h-px w-8 bg-gray-200" data-id="element-273"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-gray-200" data-id="element-274"></div>
        </div>
  }, {
    title: 'Reader gets a useful next step',
    Visual: () => <div className="flex items-center justify-center gap-3 w-full" data-id="element-275">
          <div className="w-3 h-3 rounded-full bg-seaglass" data-id="element-276"></div>
          <div className="h-1 flex-1 max-w-[70px] rounded-full bg-seaglass" data-id="element-277"></div>
          <div className="px-3 py-1.5 rounded-lg bg-sand/15 border border-sand text-xs font-semibold text-sand-dark" data-id="element-278">
            Next step
          </div>
        </div>
  }, {
    title: 'Reader applies the idea',
    Visual: () => <div className="flex items-center justify-center gap-3 w-full" data-id="element-279">
          <div className="w-9 h-11 rounded-md bg-navy" data-id="element-280"></div>
          <div className="flex flex-col gap-1.5" data-id="element-281">
            <div className="h-1.5 w-14 rounded-full bg-seaglass/50" data-id="element-282"></div>
            <div className="h-1.5 w-10 rounded-full bg-seaglass/40" data-id="element-283"></div>
            <div className="h-1.5 w-12 rounded-full bg-seaglass/30" data-id="element-284"></div>
          </div>
        </div>
  }, {
    title: 'You see what mattered',
    Visual: () => <div className="flex items-center justify-center gap-2 w-full" data-id="element-285">
          {[6, 10, 7, 12, 8].map((h, i) => <div key={i} className={`w-2.5 rounded-full ${i === 3 ? 'bg-seaglass' : 'bg-gray-200'}`} style={{
        height: `${h * 3}px`
      }} data-id="element-286"></div>)}
        </div>
  }];
  return <section className="py-24 bg-paper relative" data-id="element-287">
      <div className="absolute -top-12 left-1/2 -translate-x-1/2" data-id="element-288">
        <PathThread data-id="element-289" />
      </div>
      <div className="max-w-5xl mx-auto px-6 space-y-32" data-id="element-290">
        {/* 8A: The Build — system hub */}
        <div data-id="element-291">
          <Reveal data-id="element-292">
            <h2 className="text-3xl md:text-5xl font-serif text-navy mb-8 text-center leading-tight" data-id="element-293">
              The build:{' '}
              <span className="italic text-seaglass" data-id="element-294">Reader-to-Revenue</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} data-id="element-295">
            <div className="text-lg text-text-muted leading-relaxed max-w-2xl mx-auto text-center mb-16 space-y-5" data-id="element-296">
              <p data-id="element-297">
                A Reader-to-Revenue Build is the done-for-you system behind the
                book.
              </p>
              <p data-id="element-298">
                It gives serious readers a useful next step after the book
                creates trust.
              </p>
              <p data-id="element-299">
                It helps them raise their hand before they are ready for a sales
                call.
              </p>
              <p data-id="element-300">
                And it helps you see what they cared about, what they may need,
                and where they might fit.
              </p>
              <p className="font-serif text-2xl text-navy pt-3" data-id="element-301">
                The point is not to turn the book into a funnel.
                <br className="hidden md:block" data-id="element-302" />
                The point is to give serious readers a better next step than
                "buy the book," "join my list," or "book a call."
              </p>
            </div>
          </Reveal>

          {/* Hub and spokes */}
          <Reveal delay={0.2} data-id="element-303">
            <div className="relative" data-id="element-304">
              {/* Desktop: 3 left / hub / 3 right */}
              <div className="hidden md:grid grid-cols-[1fr_auto_1fr] items-center gap-0" data-id="element-305">
                <div className="flex flex-col gap-6 items-end" data-id="element-306">
                  {buildComponents.slice(0, 3).map(({
                  label,
                  icon: Icon
                }) => <div key={label} className="flex items-center gap-0 w-full justify-end" data-id="element-307">
                      <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-2xl pl-4 pr-5 py-3.5 shadow-[0_8px_20px_-14px_rgba(26,40,56,0.25)]" data-id="element-308">
                        <span className="w-8 h-8 rounded-full bg-seaglass/12 text-seaglass flex items-center justify-center flex-shrink-0" data-id="element-309">
                          <Icon className="w-4 h-4" data-id="element-310" />
                        </span>
                        <span className="text-sm font-medium text-navy text-right" data-id="element-311">
                          {label}
                        </span>
                      </div>
                      <div className="w-8 h-px bg-seaglass/40" data-id="element-312"></div>
                    </div>)}
                </div>

                <div className="px-2" data-id="element-313">
                  <div className="w-44 h-44 rounded-full bg-navy text-paper flex flex-col items-center justify-center text-center px-6 border-4 border-seaglass shadow-[0_24px_50px_-20px_rgba(26,40,56,0.5)]" data-id="element-314">
                    <span className="text-[0.65rem] uppercase tracking-[0.2em] text-seaglass-light mb-1.5" data-id="element-315">
                      The system
                    </span>
                    <span className="font-serif text-xl leading-tight" data-id="element-316">
                      Reader-to-Revenue Build
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-6 items-start" data-id="element-317">
                  {buildComponents.slice(3).map(({
                  label,
                  icon: Icon
                }) => <div key={label} className="flex items-center gap-0 w-full" data-id="element-318">
                      <div className="w-8 h-px bg-seaglass/40" data-id="element-319"></div>
                      <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-2xl pl-4 pr-5 py-3.5 shadow-[0_8px_20px_-14px_rgba(26,40,56,0.25)]" data-id="element-320">
                        <span className="w-8 h-8 rounded-full bg-seaglass/12 text-seaglass flex items-center justify-center flex-shrink-0" data-id="element-321">
                          <Icon className="w-4 h-4" data-id="element-322" />
                        </span>
                        <span className="text-sm font-medium text-navy" data-id="element-323">
                          {label}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Mobile: hub on top, connector, 2-col component grid */}
              <div className="md:hidden flex flex-col items-center" data-id="element-324">
                <div className="w-40 h-40 rounded-full bg-navy text-paper flex flex-col items-center justify-center text-center px-6 border-4 border-seaglass shadow-[0_20px_40px_-18px_rgba(26,40,56,0.5)]" data-id="element-325">
                  <span className="text-[0.6rem] uppercase tracking-[0.2em] text-seaglass-light mb-1.5" data-id="element-326">
                    The system
                  </span>
                  <span className="font-serif text-lg leading-tight" data-id="element-327">
                    Reader-to-Revenue Build
                  </span>
                </div>
                <div className="w-px h-8 bg-seaglass/40" data-id="element-328"></div>
                <div className="grid grid-cols-2 gap-3 w-full" data-id="element-329">
                  {buildComponents.map(({
                  label,
                  icon: Icon
                }) => <div key={label} className="flex items-center gap-2.5 bg-white border border-gray-200 rounded-2xl px-3.5 py-3 shadow-[0_8px_20px_-14px_rgba(26,40,56,0.25)]" data-id="element-330">
                      <span className="w-7 h-7 rounded-full bg-seaglass/12 text-seaglass flex items-center justify-center flex-shrink-0" data-id="element-331">
                        <Icon className="w-3.5 h-3.5" data-id="element-332" />
                      </span>
                      <span className="text-xs font-medium text-navy leading-tight" data-id="element-333">
                        {label}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* 8B: The Path After the Book Comes First */}
        <div data-id="element-334">
          <Reveal data-id="element-335">
            <h2 className="text-3xl md:text-5xl font-serif text-navy mb-8 text-center leading-tight" data-id="element-336">
              The path after the book <br data-id="element-337" />
              <span className="italic text-seaglass" data-id="element-338">usually comes first</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} data-id="element-339">
            <p className="text-lg text-text-muted leading-relaxed max-w-2xl mx-auto text-center mb-16" data-id="element-340">
              Once the path after the book is clear, bigger business questions
              become easier. Should this become a course? A certification? A
              workshop? An event? An advisory path? A team implementation model?
              A stronger sales system? A better content system?
            </p>
          </Reveal>

          <Reveal delay={0.2} data-id="element-341">
            <div className="relative pt-8 pb-12" data-id="element-342">
              <div className="bg-paper-dark rounded-3xl p-10 pt-20 border border-gray-200 shadow-inner relative mt-12" data-id="element-343">
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-paper-dark px-4 py-1 rounded-full text-xs font-semibold uppercase tracking-wider text-text-muted border border-gray-200" data-id="element-344">
                  Layer 2: What this can clarify next
                </div>
                <div className="flex flex-wrap justify-center gap-3 lg:grid lg:grid-cols-4 lg:gap-3" data-id="element-345">
                  {futureQuestions.map((q, idx) => <div key={idx} className="rounded-full border border-gray-100 bg-white px-4 py-2 text-center text-sm text-text-muted shadow-sm lg:px-2 lg:text-xs lg:whitespace-nowrap" data-id="element-346">
                      {q}
                    </div>)}
                </div>
              </div>

              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-md" data-id="element-347">
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_16px_30px_-10px_rgba(26,40,56,0.15)] relative z-10 text-center" data-id="element-348">
                  <div className="text-xs font-semibold uppercase tracking-wider text-seaglass mb-3" data-id="element-349">
                    Layer 1
                  </div>
                  <div className="font-serif text-xl text-navy" data-id="element-350">
                    The path after the book
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* 8C: Product Proof — designed conceptual cards */}
        <div data-id="element-351">
          <Reveal data-id="element-352">
            <h2 className="text-3xl md:text-5xl font-serif text-navy mb-16 text-center leading-tight" data-id="element-353">
              What this can{' '}
              <span className="italic text-seaglass" data-id="element-354">look like</span>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-6" data-id="element-355">
            {proof.map(({
            title,
            Visual
          }, idx) => <Reveal key={idx} delay={0.1 * idx} data-id="element-356">
                <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_12px_28px_-16px_rgba(26,40,56,0.15)] overflow-hidden h-full" data-id="element-357">
                  <div className="bg-paper-dark h-32 flex items-center justify-center px-8 border-b border-gray-100" data-id="element-358">
                    <Visual data-id="element-359" />
                  </div>
                  <div className="p-6" data-id="element-360">
                    <div className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-seaglass mb-2" data-id="element-361">
                      Conceptual
                    </div>
                    <div className="font-serif text-xl text-navy" data-id="element-362">{title}</div>
                  </div>
                </div>
              </Reveal>)}
          </div>
        </div>

        {/* 8D: Fit / Not Fit */}
        <div data-id="element-363">
          <Reveal data-id="element-364">
            <h2 className="text-3xl md:text-5xl font-serif text-navy mb-4 text-center leading-tight" data-id="element-365">
              Built for expert-authors with <br data-id="element-366" />
              <span className="italic text-seaglass" data-id="element-367">
                real work behind the book
              </span>
            </h2>
          </Reveal>
          <Reveal delay={0.05} data-id="element-368">
            <p className="text-center text-text-muted mb-14 max-w-xl mx-auto" data-id="element-369">
              A quick read on whether a build is worth your time.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-370">
            <Reveal delay={0.1} data-id="element-371">
              <div className="bg-white rounded-3xl p-9 md:p-10 border border-seaglass/40 shadow-[0_16px_36px_-20px_rgba(139,168,159,0.6)] h-full" data-id="element-372">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass mb-2" data-id="element-373">
                  Good fit
                </div>
                <h3 className="text-2xl font-serif text-navy mb-7" data-id="element-374">
                  This is for you if...
                </h3>
                <ul className="space-y-4 text-text" data-id="element-375">
                  {['Your book is already in the world', 'Your book supports a business, not just a royalty stream', 'You have a method, framework, point of view, or body of work', 'You sell, or want to sell, higher-value work', 'Your book points toward consulting, workshops, speaking, coaching, advisory, courses, certification, events, community, or team implementation'].map(item => <li key={item} className="flex gap-3.5 items-start" data-id="element-376">
                      <span className="w-6 h-6 rounded-full bg-seaglass text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold" data-id="element-377">
                        ✓
                      </span>
                      <span className="leading-relaxed" data-id="element-378">{item}</span>
                    </li>)}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.2} data-id="element-379">
              <div className="bg-paper-dark rounded-3xl p-9 md:p-10 border border-gray-200 h-full" data-id="element-380">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-text-muted mb-2" data-id="element-381">
                  Not a fit
                </div>
                <h3 className="text-2xl font-serif text-navy mb-7" data-id="element-382">
                  This is not for you if...
                </h3>
                <ul className="space-y-4 text-text-muted" data-id="element-383">
                  {['You only want to sell more books', 'You want bestseller help', 'You want PR or book ads', 'You want a cheap author website', 'You want a generic funnel', 'You have no real offer, method, or business behind the book'].map(item => <li key={item} className="flex gap-3.5 items-start" data-id="element-384">
                      <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold" data-id="element-385">
                        ✕
                      </span>
                      <span className="leading-relaxed" data-id="element-386">{item}</span>
                    </li>)}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>

        {/* 8E: Investment — spacious, premium, calm */}
        <Reveal data-id="element-387">
          <div className="text-center bg-navy text-white rounded-[2.5rem] px-8 py-20 md:px-16 md:py-28 shadow-2xl relative overflow-hidden" data-id="element-388">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-sand" data-id="element-389"></div>

            <div className="max-w-2xl mx-auto" data-id="element-390">
              <p className="text-sm uppercase tracking-[0.22em] text-gray-400 mb-8" data-id="element-391">
                Reader-to-Revenue Builds start at
              </p>
              <div className="font-serif font-semibold text-sand text-6xl md:text-8xl leading-none mb-14 tracking-[-0.02em]" data-id="element-392">
                $12,500
              </div>

              <div className="text-lg text-gray-300 leading-relaxed space-y-5 mb-14" data-id="element-393">
                <p data-id="element-394">This is not a low-ticket book funnel.</p>
                <p data-id="element-395">It is not a prettier author website.</p>
                <p data-id="element-396">It is not a pile of bonus resources.</p>
                <p className="text-paper" data-id="element-397">
                  It is a business layer behind the authority your book already
                  created.
                </p>
                <p data-id="element-398">
                  The economics make sense when the book supports high-value
                  work: consulting, workshops, advisory, speaking, courses,
                  certification, events, or team implementation.
                </p>
              </div>

              <p className="text-xl md:text-2xl font-serif text-paper leading-snug mb-14" data-id="element-399">
                The question is not whether every reader becomes revenue. They
                will not.{' '}
                <span className="italic text-sand" data-id="element-400">
                  The question is whether serious readers deserve a better next
                  step than disappearing after the book.
                </span>
              </p>

              <Cta variant="primary" showArrow onClick={handleCtaClick} className="px-10 py-4 text-lg shadow-[0_14px_34px_-10px_rgba(212,165,116,0.6)] hover:-translate-y-0.5" data-id="element-401">
                {ctaLabel}
              </Cta>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}